import {
 endOfMonth,
 endOfWeek,
 startOfMonth,
 startOfWeek,
 subDays,
 subMonths,
 subWeeks,
} from 'date-fns'
import { dateToLocalDateString } from './dateTime'
import { businessWeekStartsOn } from './businessWeek'
import { getUserPreference, type SemanticDatePreset } from 'services/userPreferences'

export type SemanticDateRange = { from: string | null; to: string | null }

/** Resolve a remembered semantic filter against today's local calendar. */
export function semanticDatePresetRange(
 preset: SemanticDatePreset,
 now = new Date(),
 weekStartsOn: 0 | 1 | 6 = businessWeekStartsOn(),
): SemanticDateRange {
 const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
 const to = dateToLocalDateString(today)
 if (preset === 'all') return { from: null, to: null }
 if (preset === 'today') return { from: to, to }
 if (preset === 'yesterday') {
  const yesterday = dateToLocalDateString(subDays(today, 1))
  return { from: yesterday, to: yesterday }
 }
 if (preset === 'last24Hours') return { from: dateToLocalDateString(subDays(today, 1)), to }
 if (preset === 'last7Days') return { from: dateToLocalDateString(subDays(today, 6)), to }
 if (preset === 'last30Days') return { from: dateToLocalDateString(subDays(today, 29)), to }
 if (preset === 'last90Days') return { from: dateToLocalDateString(subDays(today, 89)), to }
 if (preset === 'thisWeek') {
  return { from: dateToLocalDateString(startOfWeek(today, { weekStartsOn })), to }
 }
 if (preset === 'lastWeek') {
  const previousWeek = subWeeks(today, 1)
  return {
   from: dateToLocalDateString(startOfWeek(previousWeek, { weekStartsOn })),
   to: dateToLocalDateString(endOfWeek(previousWeek, { weekStartsOn })),
  }
 }
 if (preset === 'thisMonth') return { from: dateToLocalDateString(startOfMonth(today)), to }
 const previousMonth = subMonths(today, 1)
 return {
  from: dateToLocalDateString(startOfMonth(previousMonth)),
  to: dateToLocalDateString(endOfMonth(previousMonth)),
 }
}

/** Resolve a report's initial date range from the user's saved preset or its safe page default. */
export function getInitialReportDateRange(
 fallbackPreset: SemanticDatePreset = 'all',
 now = new Date(),
): SemanticDateRange {
 const savedPreset = getUserPreference('reports.datePreset')
 return semanticDatePresetRange(savedPreset ?? fallbackPreset, now, businessWeekStartsOn())
}
